import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { Projects } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { DevCenterClient } from "../devCenterClient";
import {
  CustomizationTaskDefinition,
  ProjectsListCustomizationTaskDefinitionsNextOptionalParams,
  ProjectsListCustomizationTaskDefinitionsOptionalParams,
  ProjectsListCustomizationTaskDefinitionsResponse,
  ProjectsGetCustomizationTaskDefinitionOptionalParams,
  ProjectsGetCustomizationTaskDefinitionResponse,
  CustomizationTaskList,
  ProjectsValidateCustomizationTasksOptionalParams,
  ProjectsValidateCustomizationTasksResponse,
  ProjectsListCustomizationTaskDefinitionsNextResponse,
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing Projects operations. */
export class ProjectsImpl implements Projects {
  private readonly client: DevCenterClient;

  /**
   * Initialize a new instance of the class Projects class.
   * @param client Reference to the service client
   */
  constructor(client: DevCenterClient) {
    this.client = client;
  }

  /**
   * Lists all customization tasks available to the project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listCustomizationTaskDefinitions(
    projectName: string,
    options?: ProjectsListCustomizationTaskDefinitionsOptionalParams,
  ): PagedAsyncIterableIterator<CustomizationTaskDefinition> {
    const iter = this.listCustomizationTaskDefinitionsPagingAll(
      projectName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCustomizationTaskDefinitionsPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCustomizationTaskDefinitionsPagingPage(
    projectName: string,
    options?: ProjectsListCustomizationTaskDefinitionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<CustomizationTaskDefinition[]> {
    let result: ProjectsListCustomizationTaskDefinitionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCustomizationTaskDefinitions(
        projectName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCustomizationTaskDefinitionsNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCustomizationTaskDefinitionsPagingAll(
    projectName: string,
    options?: ProjectsListCustomizationTaskDefinitionsOptionalParams,
  ): AsyncIterableIterator<CustomizationTaskDefinition> {
    for await (const page of this.listCustomizationTaskDefinitionsPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all customization tasks available to the project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listCustomizationTaskDefinitions(
    projectName: string,
    options?: ProjectsListCustomizationTaskDefinitionsOptionalParams,
  ): Promise<ProjectsListCustomizationTaskDefinitionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listCustomizationTaskDefinitionsOperationSpec,
    );
  }

  /**
   * Gets a customization task.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param catalogName The name of the catalog
   * @param taskName A customization task name.
   * @param options The options parameters.
   */
  getCustomizationTaskDefinition(
    projectName: string,
    catalogName: string,
    taskName: string,
    options?: ProjectsGetCustomizationTaskDefinitionOptionalParams,
  ): Promise<ProjectsGetCustomizationTaskDefinitionResponse> {
    return this.client.sendOperationRequest(
      { projectName, catalogName, taskName, options },
      getCustomizationTaskDefinitionOperationSpec,
    );
  }

  /**
   * Validates a list of customization tasks.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param body Customization tasks to validate.
   * @param options The options parameters.
   */
  validateCustomizationTasks(
    projectName: string,
    body: CustomizationTaskList,
    options?: ProjectsValidateCustomizationTasksOptionalParams,
  ): Promise<ProjectsValidateCustomizationTasksResponse> {
    return this.client.sendOperationRequest(
      { projectName, body, options },
      validateCustomizationTasksOperationSpec,
    );
  }

  /**
   * ListCustomizationTaskDefinitionsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListCustomizationTaskDefinitions method.
   * @param options The options parameters.
   */
  private _listCustomizationTaskDefinitionsNext(
    projectName: string,
    nextLink: string,
    options?: ProjectsListCustomizationTaskDefinitionsNextOptionalParams,
  ): Promise<ProjectsListCustomizationTaskDefinitionsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listCustomizationTaskDefinitionsNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listCustomizationTaskDefinitionsOperationSpec: coreClient.OperationSpec =
  {
    path: "/projects/{projectName}/customizationTasks",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.CustomizationTaskDefinitionListResult,
      },
      default: {
        bodyMapper: Mappers.CloudError,
      },
    },
    queryParameters: [Parameters.apiVersion, Parameters.filter],
    urlParameters: [Parameters.endpoint, Parameters.projectName1],
    headerParameters: [Parameters.accept],
    serializer,
  };
const getCustomizationTaskDefinitionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/catalogs/{catalogName}/customizationTasks/{taskName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationTaskDefinition,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.catalogName,
    Parameters.taskName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const validateCustomizationTasksOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/customizationTasks:validateGroup",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.CustomizationTaskListValidationOperationResult,
      headersMapper: Mappers.ProjectsValidateCustomizationTasksHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.body3,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName1],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listCustomizationTaskDefinitionsNextOperationSpec: coreClient.OperationSpec =
  {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.CustomizationTaskDefinitionListResult,
      },
      default: {
        bodyMapper: Mappers.CloudError,
      },
    },
    urlParameters: [
      Parameters.endpoint,
      Parameters.projectName1,
      Parameters.nextLink,
    ],
    headerParameters: [Parameters.accept],
    serializer,
  };
